/**
 * LCW — Preço dinâmico da PDP.
 *
 * Mantém preço total, riscado, % OFF, valor no Pix e parcelamento (snippet `lcw-payment-info`)
 * em sincronia com o que o cliente escolheu:
 *   1. Kaching Bundles — evento `variants-changed` (bubbles) disparado por <kaching-bundles-block>
 *      com `pricing.discountedPrice.amount` / `pricing.fullPrice.amount` em centavos e as quantidades;
 *   2. troca de variante nativa do Horizon — `shopify:product:select` (variant-picker);
 *   3. seletor de quantidade nativo — `quantity-selector:update` (bloco `quantity`, se habilitado).
 *
 * É carregado pelo próprio bloco (`{% render 'lcw-payment-info', part: 'price' %}`), sem editar arquivos nativos.
 * Cálculo de Pix/parcelas espelha o Liquid do snippet (divisão inteira) para não "pular" centavos na hidratação.
 */
import { formatMoney, convertMoneyToMinorUnits } from '@theme/money-formatting';

const KACHING_EVENT = 'variants-changed';
const PRODUCT_SELECT_EVENT = 'shopify:product:select';
const QUANTITY_EVENT = 'quantity-selector:update';

/** @type {WeakMap<Element, {unitPrice:number, unitCompare:number, quantity:number, total:number, totalFull:number}>} */
const states = new WeakMap();

/** @param {Element} el */
const scopeOf = (el) => el.closest('.shopify-section') || document.body;

/** @param {Element} scope */
function config(scope) {
  const priceEl = scope.querySelector('[data-lcw-price-part="price"]');
  const linesEl = scope.querySelector('[data-lcw-price-part="lines"]');
  const any = priceEl || linesEl;
  if (!any) return null;
  let variants = [];
  try {
    variants = JSON.parse(priceEl?.querySelector('script[data-lcw-price-variants]')?.textContent || '[]');
  } catch (_) {
    /* JSON inválido: segue sem tabela de variantes */
  }
  return {
    format: any.dataset.moneyFormat || '{{amount}}',
    currency: any.dataset.currency || 'BRL',
    installments: Number(linesEl?.dataset.installments || 10),
    minInstallment: Number(linesEl?.dataset.minInstallment || 500),
    pixDiscount: Number(linesEl?.dataset.pixDiscount || 0),
    variants,
  };
}

/** @param {Element} scope */
function state(scope) {
  let st = states.get(scope);
  if (st) return st;
  const priceEl = scope.querySelector('[data-lcw-price-part="price"]');
  const unitPrice = Number(priceEl?.dataset.price || 0);
  const unitCompare = Number(priceEl?.dataset.compareAt || unitPrice);
  st = { unitPrice, unitCompare, quantity: 1, total: unitPrice, totalFull: unitCompare };
  states.set(scope, st);
  return st;
}

/** Nº de parcelas sem juros respeitando o valor mínimo da parcela — igual ao loop do snippet. */
function installmentsFor(total, cfg) {
  let n = cfg.installments;
  while (n > 1 && Math.floor(total / n) < cfg.minInstallment) n -= 1;
  return Math.max(n, 1);
}

/** @param {Element} scope */
function render(scope) {
  const cfg = config(scope);
  if (!cfg) return;
  const st = state(scope);
  const fmt = (cents) => formatMoney(cents, cfg.format, cfg.currency);
  const { total, totalFull } = st;
  const hasCompare = totalFull > total;
  const off = hasCompare ? Math.floor(((totalFull - total) * 100) / totalFull) : 0;

  scope.querySelectorAll('[data-lcw-price-current]').forEach((el) => (el.textContent = fmt(total)));
  scope.querySelectorAll('[data-lcw-price-compare]').forEach((el) => {
    el.hidden = !hasCompare;
    const bdi = el.querySelector('bdi');
    if (bdi) bdi.textContent = fmt(totalFull);
  });
  scope.querySelectorAll('[data-lcw-price-off]').forEach((el) => {
    el.hidden = off <= 0;
    el.textContent = `${off}% OFF`;
  });
  if (cfg.pixDiscount > 0) {
    const pix = Math.floor((total * (100 - cfg.pixDiscount)) / 100);
    scope.querySelectorAll('[data-lcw-price-pix]').forEach((el) => (el.textContent = fmt(pix)));
  }
  const n = installmentsFor(total, cfg);
  scope.querySelectorAll('[data-lcw-price-installments]').forEach((el) => {
    el.hidden = n < 2;
    const nEl = el.querySelector('[data-lcw-price-installments-n]');
    const vEl = el.querySelector('[data-lcw-price-installments-value]');
    if (nEl) nEl.textContent = String(n);
    if (vEl) vEl.textContent = fmt(Math.floor(total / n));
  });
}

/** Recalcula o total a partir de variante × quantidade (sem Kaching). */
function recomputeFromUnit(scope) {
  const st = state(scope);
  st.total = st.unitPrice * st.quantity;
  st.totalFull = st.unitCompare * st.quantity;
  render(scope);
}

/** Kaching Bundles: usa o preço já calculado pelo app (inclui desconto do bundle). */
function applyKaching(scope, detail) {
  const pricing = detail?.pricing;
  const discounted = Number(pricing?.discountedPrice?.amount);
  if (!Number.isFinite(discounted)) return;
  const st = state(scope);
  const full = Number(pricing?.fullPrice?.amount);
  const qty = (detail.variantIdQuantities || []).reduce((sum, v) => sum + Number(v.quantity || 0), 0);
  st.quantity = qty || st.quantity;
  st.total = discounted;
  st.totalFull = Number.isFinite(full) && full > discounted ? full : discounted;
  render(scope);
}

/**
 * Fallback para quando o Kaching já montou o widget antes deste módulo rodar (bar pré-selecionada
 * sem termos ouvido o evento): lê o preço da bar selecionada e a quantidade do form.
 */
function seedFromKachingDom(scope) {
  const bar = scope.querySelector('.kaching-bundles__bar--selected');
  if (!bar) return;
  const cfg = config(scope);
  if (!cfg) return;
  const priceText = bar.querySelector('.kaching-bundles__bar-price')?.textContent || '';
  const fullText = bar.querySelector('.kaching-bundles__bar-full-price')?.textContent || '';
  const discounted = convertMoneyToMinorUnits(priceText, cfg.currency);
  if (discounted == null) return;
  const full = convertMoneyToMinorUnits(fullText, cfg.currency);
  const qtyInput = scope.querySelector('form[action*="/cart/add"] input[name="quantity"]');
  applyKaching(scope, {
    pricing: { discountedPrice: { amount: discounted }, fullPrice: { amount: full ?? discounted } },
    variantIdQuantities: [{ quantity: Number(qtyInput?.value || 1) }],
  });
}

document.addEventListener(KACHING_EVENT, (event) => {
  if (!(event.target instanceof Element)) return;
  applyKaching(scopeOf(event.target), /** @type {CustomEvent} */ (event).detail);
});

document.addEventListener(PRODUCT_SELECT_EVENT, (event) => {
  if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
  const scope = scopeOf(event.target);
  const cfg = config(scope);
  if (!cfg) return;
  const promise = /** @type {any} */ (event).promise;
  if (!promise?.then) return;
  promise
    .then(({ variant }) => {
      const id = String(variant?.id ?? '').replace(/\D+$/, '').split('/').pop();
      const found = cfg.variants.find((v) => String(v.id) === id);
      if (!found) return;
      const st = state(scope);
      st.unitPrice = Number(found.price);
      st.unitCompare = Math.max(Number(found.compare_at_price || found.price), st.unitPrice);
      // O Kaching dispara `variants-changed` logo em seguida e sobrescreve com o preço do bundle.
      recomputeFromUnit(scope);
    })
    .catch(() => {});
});

document.addEventListener(QUANTITY_EVENT, (event) => {
  if (!(event.target instanceof Element) || event.target.closest('cart-items-component, .cart-drawer')) return;
  const scope = scopeOf(event.target);
  if (!config(scope)) return;
  const qty = Number(/** @type {CustomEvent} */ (event).detail?.quantity);
  if (!Number.isFinite(qty) || qty < 1) return;
  state(scope).quantity = qty;
  recomputeFromUnit(scope);
});

// Estado inicial: hidrata a partir do DOM e, se o Kaching já pré-selecionou uma oferta, adota o preço dela.
document.querySelectorAll('[data-lcw-price-part="price"]').forEach((el) => {
  const scope = scopeOf(el);
  seedFromKachingDom(scope);
});
